import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'react-feather';

const slides = [
  {
    title: 'Own a Piece of Real Gas Reserves.',
    subtitle: 'Step into the $200B+ energy market and trade virtual gas backed by actual reserves.',
    cta: 'Join the Waitlist',
    orbColor: 'rgba(247,147,30,0.4)',
    gradient: 'linear-gradient(135deg, rgba(247,147,30,0.1), rgba(255,255,255,0.05))',
  },
  {
    title: 'Trade Energy.',
    subtitle: 'No pipelines. No middlemen. Just instant exposure to real energy assets.',
    cta: 'Start Trading Soon',
    orbColor: 'rgba(255,255,255,0.25)',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(0,0,0,0.4))',
  },
  {
    title: 'Invest Early. Profit Big.',
    subtitle: 'Be among the first to access the future of virtual energy trading.',
    cta: 'Become a Founding Member',
    orbColor: 'rgba(247,147,30,0.5)',
    gradient: 'linear-gradient(135deg, rgba(247,147,30,0.15), rgba(0,0,0,0.4))',
  },
];

export function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 9000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setIndex((prev) => (prev + 1) % slides.length);
  const prev = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <div className="relative w-full min-h-[35vh] md:min-h-[90vh] flex flex-col items-center justify-center overflow-hidden">
      <AnimatePresence mode="wait">
        {slides.map((slide, i) =>
          i === index ? (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 150 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -150 }}
              transition={{ duration: 1 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.8}
              onDragEnd={(e, { offset, velocity }) => {
                if (offset.x < -100 || velocity.x < -500) {
                  next();
                } else if (offset.x > 100 || velocity.x > 500) {
                  prev();
                }
              }}
              className="absolute inset-0 flex flex-col md:flex-row items-center justify-between px-8 md:px-24"
            >
              {/* Background Gradient Aura */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.3 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="absolute inset-0 z-0"
                style={{
                  background: slide.gradient,
                  filter: 'blur(80px)',
                }}
              />

              {/* Left: Text */}
              <div className="max-w-xl z-10 text-center md:text-left">
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="text-3xl sm:text-4xl md:text-6xl font-bold text-white leading-snug"
                >
                  {slide.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="mt-4 text-base sm:text-lg md:text-xl text-gray-300"
                >
                  {slide.subtitle}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="mt-6 sm:mt-8"
                >
                  <Link
                    to="/waitlist"
                    className="px-6 py-3 bg-[#F7931E] text-black font-bold rounded-lg glow-hover block mx-auto md:inline-block"
                  >
                    {slide.cta}
                  </Link>
                </motion.div>
              </div>

              {/* Right: Orb - Desktop vs Mobile */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.8 }}
                transition={{ delay: 0.5, duration: 1 }}
                className=" relative flex-1 hidden md:flex justify-center items-center mt-8 md:mt-0"
              >
                {/* Mobile: Small pulsing orb */}
                <div className="block md:hidden relative w-[100px] h-[100px] rounded-full">
                  <div
                    className="absolute inset-0 rounded-full blur-xl"
                    style={{
                      background: `radial-gradient(circle, ${slide.orbColor}, transparent 70%)`,
                      animation: 'pulse 6s infinite alternate',
                    }}
                  />
                </div>

                {/* Desktop: Large orb with waves + particles */}
                <div className="hidden md:block relative w-[350px] h-[350px] lg:w-[450px] lg:h-[450px]">
                  <div
                    className="absolute inset-0 rounded-full blur-3xl"
                    style={{
                      background: `radial-gradient(circle, ${slide.orbColor}, transparent 70%)`,
                      animation: 'pulse 6s infinite alternate',
                    }}
                  />
                  {[...Array(3)].map((_, waveIndex) => (
                    <motion.div
                      key={waveIndex}
                      initial={{ scale: 0.5, opacity: 0.4 }}
                      animate={{ scale: 2, opacity: 0 }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        delay: waveIndex * 1.5,
                      }}
                      className="absolute inset-0 rounded-full border border-[#F7931E]/40"
                    />
                  ))}
                  {[...Array(6)].map((_, pIndex) => (
                    <motion.div
                      key={pIndex}
                      initial={{ y: 0, opacity: 0.3 }}
                      animate={{ y: ['-10%', '10%', '-10%'], opacity: [0.2, 0.5, 0.2] }}
                      transition={{
                        repeat: Infinity,
                        duration: 10 + pIndex * 2,
                        ease: 'easeInOut',
                        delay: pIndex,
                      }}
                      className="absolute rounded-full blur-lg"
                      style={{
                        width: `${Math.random() * 30 + 15}px`,
                        height: `${Math.random() * 30 + 15}px`,
                        top: `${Math.random() * 80}%`,
                        left: `${Math.random() * 80}%`,
                        background: slide.orbColor,
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ) : null
        )}
      </AnimatePresence>

      {/* Navigation Arrows (desktop) */}

      {/* Slide Indicators (clickable) */}
      <div className="hidden absolute bottom-6 w-full md:flex justify-center space-x-3 z-20">
        {slides.map((_, i) => (
          <motion.div
            key={i}
            onClick={() => setIndex(i)}
            animate={{
              scale: i === index ? 1.3 : 1,
              opacity: i === index ? 1 : 0.5,
            }}
            className="w-4 h-4 rounded-full bg-[#F7931E] transition cursor-pointer"
          />
        ))}
      </div>
    </div>
  );
}
